/**
 * The open tab's list on Home.
 *
 * @module components/home/HomeTabList
 */
import type { FindingItem } from "../regulation/finding/findingItem";
import { HomeFindingGroups } from "./HomeFindingGroups";
import { HomeFindingRow } from "./HomeFindingRow";
import type { HomeFindingGroup, HomeFindingsStatus } from "./HomeFindings.logic";
import type { HomeGlance } from "./homeGlance.logic";
import { HomeFindingsPending, HomeListEmpty } from "./HomeListEmpty";
import type { HomeComingUpItem } from "./homeLogic";
import { HomeScheduledList } from "./HomeScheduledList";
import type { HomeTab } from "./homeTabs.logic";
import { HomeUpdatesList } from "./HomeUpdatesList";
import { HomeWorkflowsList } from "./HomeWorkflowsList";

export interface HomeListsData {
  readonly orgSegment: string;
  readonly now: number;
  readonly glance: HomeGlance;
  readonly findings: ReadonlyArray<FindingItem>;
  readonly findingGroups: ReadonlyArray<HomeFindingGroup>;
  readonly findingsStatus: HomeFindingsStatus;
  readonly onRetryFindings: () => void;
  readonly comingUp: ReadonlyArray<HomeComingUpItem>;
  /** Fill the composer with this request, e.g. Scheduled's "Prepare with Amoreg". */
  readonly onPrepare: (prompt: string) => void;
}

export function HomeTabList({ tab, data }: { readonly tab: HomeTab; readonly data: HomeListsData }) {
  const { orgSegment, now, glance } = data;
  switch (tab) {
    case "mine":
      if (data.findingsStatus !== "ready") return <HomeFindingsPending status={data.findingsStatus} onRetry={data.onRetryFindings} />;
      if (glance.mine.length === 0) return <HomeListEmpty>Nothing assigned to you</HomeListEmpty>;
      return (
        <ul className="flex flex-col">
          {glance.mine.map((item) => (
            <HomeFindingRow key={item.id} item={item} orgSegment={orgSegment} now={now} withDue />
          ))}
        </ul>
      );
    case "findings":
      if (data.findingsStatus !== "ready") return <HomeFindingsPending status={data.findingsStatus} onRetry={data.onRetryFindings} />;
      return <HomeFindingGroups groups={data.findingGroups} orgSegment={orgSegment} now={now} />;
    case "due":
      return <HomeScheduledList items={data.comingUp} orgSegment={orgSegment} now={now} onPrepare={data.onPrepare} />;
    case "updates":
      return <HomeUpdatesList glance={glance} orgSegment={orgSegment} now={now} />;
    case "workflows":
      return <HomeWorkflowsList glance={glance} orgSegment={orgSegment} now={now} />;
  }
}
